import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronRight,
  CreditCard as CreditCardIcon,
  Loader2,
  Minus,
  Plus,
  Repeat,
  Repeat1,
} from 'lucide-react';
import {
  currentMonthSP,
  effDay,
  formatMonthLabel,
  formatYmd,
  invoiceMonthFor,
  parseYm,
  parseYmd,
  todaySP,
  type Account,
  type AccountsResponse,
  type CardsResponse,
  type CreateEntryBody,
  type CreateTransferBody,
  type CreditCard as CreditCardModel,
  type OpenFinanceLaunch,
  type RecurrenceInput,
  type RecurrenceScope,
  type TagsResponse,
  type Transaction,
  type TransactionResponse,
  type TransactionType,
  type UpdateTransactionBody,
  type Ym,
  type Ymd,
} from '@lira/shared';
import { apiGet, apiPatch, apiPost } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { useMonthStore } from '@/stores/month';
import { toast } from '@/stores/toast';
import { MoneyInput, Select, Sheet, Toggle, resolveIcon, type SelectOption } from '@/components/ui';
import { cn } from '@/lib/cn';
import { DateField } from './DateField';
import { CategoryPicker } from './CategoryPicker';
import { RecurrenceScopeDialog } from './RecurrenceScopeDialog';
import { isLogicalTransfer, transferAccountIds } from './transfer-presentation';
import {
  CARD_EXPENSE_META,
  TYPE_META,
  getLastAccountId,
  getLastCardId,
  getLastCategoryId,
  setLastAccountId,
  setLastCardId,
  setLastCategoryId,
} from './txMeta';

type RecurrenceMode = 'none' | 'fixed' | 'installment';

interface FormState {
  amountCents: number;
  effectuated: boolean;
  date: Ymd;
  description: string;
  categoryId: string | null;
  accountId: string | null;
  cardId: string | null;
  fromAccountId: string | null;
  toAccountId: string | null;
  tagIds: string[];
  note: string;
  ignoreInReports: boolean;
  recurrence: RecurrenceMode;
  installments: number;
  invoiceMonth: Ym;
}

export interface TransactionReviewDraft {
  amountCents: number;
  date: Ymd;
  description: string;
  categoryId: string | null;
  accountId: string | null;
  cardId: string | null;
  launch: OpenFinanceLaunch | null;
  suggestedMode: OpenFinanceLaunch['mode'];
}

/** Data default ciente do mês visualizado (doc 12). */
function defaultDate(viewedMonth: Ym): Ymd {
  if (viewedMonth === currentMonthSP()) return todaySP();
  const { year, month } = parseYm(viewedMonth);
  return formatYmd(year, month, effDay(year, month, parseYmd(todaySP()).day));
}

function initialState(
  type: TransactionType,
  editing: Transaction | null,
  viewedMonth: Ym,
  presetAccountId: string | null,
  presetCardId: string | null,
  isCard: boolean,
  reviewDraft: TransactionReviewDraft | null,
): FormState {
  if (reviewDraft) {
    return {
      amountCents: Math.abs(reviewDraft.amountCents),
      effectuated: true,
      date: reviewDraft.date,
      description: reviewDraft.description,
      categoryId: reviewDraft.categoryId,
      accountId: reviewDraft.launch?.mode === 'card_payment'
        ? reviewDraft.launch.accountId
        : reviewDraft.launch?.mode === 'income' || reviewDraft.launch?.mode === 'expense'
          ? reviewDraft.launch.accountId : reviewDraft.accountId,
      cardId: reviewDraft.launch?.mode === 'card_payment'
        ? reviewDraft.launch.cardId
        : reviewDraft.launch?.mode === 'income' || reviewDraft.launch?.mode === 'expense'
          ? reviewDraft.launch.cardId : reviewDraft.cardId,
      fromAccountId: reviewDraft.launch?.mode === 'transfer'
        ? reviewDraft.launch.fromAccountId
        : reviewDraft.suggestedMode === 'transfer' && reviewDraft.amountCents < 0 ? reviewDraft.accountId : null,
      toAccountId: reviewDraft.launch?.mode === 'transfer'
        ? reviewDraft.launch.toAccountId
        : reviewDraft.suggestedMode === 'transfer' && reviewDraft.amountCents > 0 ? reviewDraft.accountId : null,
      tagIds: [],
      note: '',
      ignoreInReports: false,
      recurrence: 'none',
      installments: 2,
      invoiceMonth: reviewDraft.launch?.mode === 'card_payment' ? reviewDraft.launch.invoiceMonth : currentMonthSP(),
    };
  }
  if (editing) {
    const transferAccounts = transferAccountIds(editing);
    return {
      amountCents: Math.abs(editing.amountCents),
      effectuated: editing.effectuated,
      date: editing.date,
      description: editing.description,
      categoryId: editing.categoryId,
      accountId: editing.accountId,
      cardId: editing.cardId,
      fromAccountId: transferAccounts.fromAccountId,
      toAccountId: transferAccounts.toAccountId,
      tagIds: editing.tags.map((t) => t.id),
      note: editing.note ?? '',
      ignoreInReports: editing.ignoreInReports,
      recurrence: 'none',
      installments: 2,
      invoiceMonth: editing.invoiceMonth ?? currentMonthSP(),
    };
  }
  return {
    amountCents: 0,
    effectuated: true,
    date: defaultDate(viewedMonth),
    description: '',
    categoryId: type === 'transfer' ? null : getLastCategoryId(type),
    accountId: isCard ? null : (presetAccountId ?? getLastAccountId()),
    cardId: isCard ? (presetCardId ?? getLastCardId()) : null,
    fromAccountId: presetAccountId ?? getLastAccountId(),
    toAccountId: null,
    tagIds: [],
    note: '',
    ignoreInReports: false,
    recurrence: 'none',
    installments: 2,
    invoiceMonth: currentMonthSP(),
  };
}

const AMOUNT_INPUT_ID = 'tx-amount-input';
const focusAmount = () =>
  window.setTimeout(() => document.getElementById(AMOUNT_INPUT_ID)?.focus(), 60);

interface TransactionModalProps {
  open: boolean;
  type: TransactionType;
  editing: Transaction | null;
  presetAccountId: string | null;
  /** Modo "despesa no cartão" (M5): cartão em vez de conta, acento teal. */
  isCard: boolean;
  presetCardId: string | null;
  reviewDraft?: TransactionReviewDraft | null;
  reviewSaving?: boolean;
  onReviewSave?: (draft: { date: Ymd; title: string; launch: OpenFinanceLaunch }) => void;
  onClose: () => void;
}

/** Formulário de lançamento (doc 12): valor primeiro, data ciente do mês, categoria por chips. */
export function TransactionModal({
  open,
  type,
  editing,
  presetAccountId,
  isCard,
  presetCardId,
  reviewDraft = null,
  reviewSaving = false,
  onReviewSave,
  onClose,
}: TransactionModalProps) {
  const queryClient = useQueryClient();
  const viewedMonth = useMonthStore((s) => s.month);
  const isReview = reviewDraft !== null;
  const [reviewMode, setReviewMode] = useState<OpenFinanceLaunch['mode']>(() => reviewDraft?.launch?.mode ?? reviewDraft?.suggestedMode ?? 'expense');
  const effectiveType: TransactionType = isReview
    ? (reviewMode === 'income' ? 'income' : reviewMode === 'transfer' ? 'transfer' : 'expense')
    : type;
  const isTransfer = effectiveType === 'transfer';
  const cardPaymentMode = isReview && reviewMode === 'card_payment';
  const isLegacyTransfer = isTransfer && !!editing && !isLogicalTransfer(editing);
  // Despesa no cartão: modo novo (isCard) ou edição de uma transação com cartão.
  const cardMode = !cardPaymentMode && !isTransfer && (isCard || !!editing?.cardId || (isReview && reviewMode === 'expense' && !!reviewDraft?.cardId));
  const meta = cardPaymentMode || cardMode ? CARD_EXPENSE_META : TYPE_META[effectiveType];
  const isRecurringEdit = !!editing?.recurrenceId;

  const [form, setForm] = useState<FormState>(() =>
    initialState(effectiveType, editing, viewedMonth, presetAccountId, presetCardId, cardMode, reviewDraft),
  );
  const [amountTouched, setAmountTouched] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);

  // (re)inicializa ao abrir / trocar de tipo ou transação
  useEffect(() => {
    if (!open) return;
    setForm(initialState(effectiveType, editing, viewedMonth, presetAccountId, presetCardId, cardMode, reviewDraft));
    setAmountTouched(false);
    setDetailsOpen(!isReview && !!editing && (editing.tags.length > 0 || !!editing.note));
    if (!isReview) focusAmount();
    // viewedMonth intencionalmente fora das deps: fixa o mês no momento da abertura
  }, [open, effectiveType, editing, presetAccountId, presetCardId, cardMode, reviewDraft, isReview]);

  const accountsQuery = useQuery({
    queryKey: ['accounts', 'compose'],
    queryFn: () => apiGet<AccountsResponse>('/accounts'),
    enabled: open,
  });
  const cardsQuery = useQuery({
    queryKey: ['cards', viewedMonth],
    queryFn: () => apiGet<CardsResponse>(`/cards?month=${viewedMonth}`),
    enabled: open && (cardMode || cardPaymentMode),
  });
  const tagsQuery = useQuery({
    queryKey: ['tags'],
    queryFn: () => apiGet<TagsResponse>('/tags'),
    enabled: open && !isTransfer,
  });

  const accounts = useMemo(
    () => (accountsQuery.data?.accounts ?? []).filter((a) => !a.archived),
    [accountsQuery.data],
  );
  const cards = useMemo(
    () => (cardsQuery.data?.cards ?? []).filter((c) => !c.archived),
    [cardsQuery.data],
  );
  const accountOption = (a: Account): SelectOption<string> => ({
    value: a.id,
    label: a.name,
    icon: resolveIcon(a.icon),
    color: a.color,
  });
  const cardOption = (c: CreditCardModel): SelectOption<string> => ({
    value: c.id,
    label: c.name,
    icon: CreditCardIcon,
    color: c.color,
  });
  const tags = (tagsQuery.data?.tags ?? []).filter((t) => !t.archived);

  // Preview instantâneo "cai na fatura de {mês}" (motor puro compartilhado).
  const selectedCard = cards.find((c) => c.id === form.cardId);
  const previewInvoiceMonth =
    cardMode && selectedCard
      ? invoiceMonthFor(
          { closingDay: selectedCard.closingDay, dueDay: selectedCard.dueDay },
          form.date,
        )
      : null;

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  // --- validação ---
  const amountValid = form.amountCents > 0;
  const entryValid = cardPaymentMode
    ? !!form.accountId && !!form.cardId && /^\d{4}-\d{2}$/.test(form.invoiceMonth)
    : cardMode
      ? !!form.categoryId && !!form.cardId
      : !!form.categoryId && !!form.accountId;
  const transferValid = isLegacyTransfer
    ? !!form.accountId
    : !!form.fromAccountId && !!form.toAccountId && form.fromAccountId !== form.toAccountId;
  const canSave = amountValid && !!form.description.trim() && (isTransfer ? transferValid : entryValid);

  // --- corpos de requisição ---
  function recurrenceInput(): RecurrenceInput | undefined {
    if (form.recurrence === 'fixed') return { mode: 'fixed' };
    if (form.recurrence === 'installment')
      return { mode: 'installment', times: form.installments };
    return undefined;
  }

  function buildCreateBody(): CreateEntryBody | CreateTransferBody {
    const note = form.note.trim();
    const description = form.description.trim();
    if (isTransfer) {
      return {
        type: 'transfer',
        amountCents: form.amountCents,
        fromAccountId: form.fromAccountId ?? '',
        toAccountId: form.toAccountId ?? '',
        date: form.date,
        description,
        effectuated: form.effectuated,
        ...(note ? { note } : {}),
      };
    }
    const rec = recurrenceInput();
    return {
      type: type === 'income' ? 'income' : 'expense',
      amountCents: form.amountCents,
      categoryId: form.categoryId ?? '',
      // Cartão: envia cardId (o motor grava invoice_month), sem accountId.
      ...(cardMode
        ? { cardId: form.cardId ?? undefined }
        : { accountId: form.accountId ?? undefined }),
      date: form.date,
      description,
      effectuated: form.effectuated,
      ignoreInReports: form.ignoreInReports,
      ...(note ? { note } : {}),
      ...(form.tagIds.length ? { tagIds: form.tagIds } : {}),
      ...(rec ? { recurrence: rec } : {}),
    };
  }

  function buildUpdateBody(): UpdateTransactionBody {
    const body: UpdateTransactionBody = {
      amountCents: form.amountCents,
      date: form.date,
      description: form.description.trim(),
      effectuated: form.effectuated,
      note: form.note.trim() || null,
    };
    if (isTransfer) {
      if (isLegacyTransfer) {
        body.accountId = form.accountId ?? undefined;
      } else {
        body.fromAccountId = form.fromAccountId ?? undefined;
        body.toAccountId = form.toAccountId ?? undefined;
      }
    } else {
      body.categoryId = form.categoryId ?? undefined;
      if (cardMode) body.cardId = form.cardId ?? undefined;
      else body.accountId = form.accountId ?? undefined;
      body.ignoreInReports = form.ignoreInReports;
      body.tagIds = form.tagIds;
    }
    return body;
  }

  function persistLastUsed() {
    if (isTransfer) {
      if (form.fromAccountId) setLastAccountId(form.fromAccountId);
    } else {
      if (cardMode) {
        if (form.cardId) setLastCardId(form.cardId);
      } else if (form.accountId) {
        setLastAccountId(form.accountId);
      }
      if (form.categoryId) setLastCategoryId(type, form.categoryId);
    }
  }

  const mutation = useMutation({
    mutationFn: (vars: { scope: RecurrenceScope; another: boolean }) => {
      if (editing) {
        return apiPatch<TransactionResponse>(
          `/transactions/${editing.id}?scope=${vars.scope}`,
          buildUpdateBody(),
        );
      }
      return apiPost<TransactionResponse>('/transactions', buildCreateBody());
    },
    onSuccess: (_data, vars) => {
      persistLastUsed();
      void queryClient.invalidateQueries({ queryKey: ['transactions'] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['expense-calendar'] });
      // Despesa no cartão altera fatura + limite → atualiza a tela de Cartões.
      if (cardMode) void queryClient.invalidateQueries({ queryKey: ['cards'] });
      toast(editing ? 'Lançamento atualizado.' : 'Lançamento criado.', 'success');
      setScopeOpen(false);
      if (vars.another && !editing) {
        // reabre limpo mantendo conta/cartão/categoria/data (mês-base)
        setForm((f) => {
          const fresh = initialState(type, null, viewedMonth, presetAccountId, presetCardId, cardMode, null);
          return {
            ...fresh,
            accountId: f.accountId,
            cardId: f.cardId,
            categoryId: f.categoryId,
            fromAccountId: f.fromAccountId,
            toAccountId: f.toAccountId,
            date: f.date,
            effectuated: f.effectuated,
          };
        });
        setAmountTouched(false);
        focusAmount();
      } else {
        onClose();
      }
    },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const submit = (another: boolean) => {
    if (!canSave || mutation.isPending || reviewSaving) return;
    if (isReview) {
      let launch: OpenFinanceLaunch | null = null;
      if (reviewMode === 'transfer' && form.fromAccountId && form.toAccountId) {
        launch = { mode: 'transfer', fromAccountId: form.fromAccountId, toAccountId: form.toAccountId };
      } else if (reviewMode === 'card_payment' && form.accountId && form.cardId) {
        launch = { mode: 'card_payment', accountId: form.accountId, cardId: form.cardId, invoiceMonth: form.invoiceMonth };
      } else if ((reviewMode === 'income' || reviewMode === 'expense') && form.categoryId) {
        launch = { mode: reviewMode, categoryId: form.categoryId, accountId: cardMode ? null : form.accountId, cardId: cardMode ? form.cardId : null };
      }
      if (launch) onReviewSave?.({ date: form.date, title: form.description.trim(), launch });
      return;
    }
    if (editing && isRecurringEdit) {
      setScopeOpen(true);
      return;
    }
    mutation.mutate({ scope: 'one', another });
  };

  const label = meta.label.toLowerCase();
  const title = isReview ? 'Editar lançamento Open Finance' : `${editing ? 'Editar' : 'Nova'} ${label}`;

  return (
    <>
      <Sheet open={open} onClose={onClose} title={title} maxWidth={detailsOpen ? 1040 : 560}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(false);
          }}
          className="min-w-0"
        >
          <div
            data-testid="transaction-form-grid"
            data-expanded={detailsOpen}
            className={cn('grid min-w-0 gap-5', detailsOpen && 'lg:grid-cols-2')}
          >
            <div className="min-w-0 space-y-4">
          {isReview && (
            <div>
              <p className="mb-2 text-[13px] text-muted">Lançar como</p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {([
                  ['expense', 'Despesa'], ['income', 'Receita'], ['transfer', 'Transferência'], ['card_payment', 'Pagamento de fatura'],
                ] as const).map(([mode, label]) => (
                  <button key={mode} type="button" onClick={() => setReviewMode(mode)}
                    className={cn('min-h-11 rounded-lg border px-2 text-xs font-semibold transition-colors', reviewMode === mode ? 'border-primary bg-primary-weak text-primary' : 'border-border text-muted hover:text-ink')}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* VALOR — primeiro, gigante, autofocus */}
          <div className={cn('rounded-xl px-4 py-3.5', meta.soft)}>
            <label htmlFor={AMOUNT_INPUT_ID} className="mb-0.5 block text-[13px] text-muted">
              Valor
            </label>
            <MoneyInput
              id={AMOUNT_INPUT_ID}
              valueCents={form.amountCents}
              onChange={(cents) => {
                setAmountTouched(true);
                update('amountCents', cents);
              }}
              accent={meta.accent}
              size="lg"
              readOnly={isReview}
              aria-label="Valor"
            />
            {amountTouched && !amountValid && (
              <p className="mt-1 text-[13px] text-expense">O valor deve ser maior que zero.</p>
            )}
          </div>

          {/* Efetivação (não se aplica a despesa no cartão — vai direto p/ a fatura) */}
          {!cardMode && !isReview && (
            <Toggle
              checked={form.effectuated}
              onChange={(v) => update('effectuated', v)}
              accent={meta.accent}
              label={meta.effLabel}
            />
          )}

          {/* Data */}
          <DateField
            value={form.date}
            onChange={(d) => update('date', d)}
            viewedMonth={viewedMonth}
            accent={meta.accent}
          />

          {/* Descrição */}
          <div>
            <label htmlFor="tx-description" className="mb-1 block text-[13px] text-muted">
              Descrição
            </label>
            <input
              id="tx-description"
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              maxLength={120}
              placeholder={isTransfer ? 'Ex.: Reserva de emergência' : 'Ex.: Mercado, salário…'}
              autoComplete="off"
              className="h-11 w-full rounded-lg border border-field bg-surface px-3 text-[15px] text-ink transition-colors focus:border-primary focus:outline-none"
            />
          </div>

          {/* Categoria (entradas) */}
          {!isTransfer && !cardPaymentMode && (
            <CategoryPicker
              type={effectiveType}
              value={form.categoryId}
              onChange={(id) => update('categoryId', id)}
            />
          )}

          {/* Conta(s) */}
          {cardPaymentMode ? (
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-[13px] text-muted">Conta pagadora</label>
                <Select<string> value={form.accountId ?? ''} options={accounts.map(accountOption)} onChange={(id) => update('accountId', id)} placeholder="Selecione a conta" aria-label="Conta pagadora" />
              </div>
              <div>
                <label className="mb-1 block text-[13px] text-muted">Cartão</label>
                <Select<string> value={form.cardId ?? ''} options={cards.map(cardOption)} onChange={(id) => update('cardId', id)} placeholder="Selecione o cartão" aria-label="Cartão da fatura" />
              </div>
              <div>
                <label htmlFor="invoice-month" className="mb-1 block text-[13px] text-muted">Competência da fatura</label>
                <input id="invoice-month" type="month" value={form.invoiceMonth} onChange={(event) => update('invoiceMonth', event.target.value as Ym)}
                  className="h-11 w-full rounded-lg border border-field bg-surface px-3 text-[15px] text-ink focus:border-primary focus:outline-none" />
              </div>
            </div>
          ) : isTransfer && !isLegacyTransfer ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-[13px] text-muted">Conta de origem</label>
                <Select<string>
                  value={form.fromAccountId ?? ''}
                  options={accounts.filter((a) => a.id !== form.toAccountId).map(accountOption)}
                  onChange={(id) => update('fromAccountId', id)}
                  placeholder="Selecione"
                  aria-label="Conta de origem"
                />
              </div>
              <div>
                <label className="mb-1 block text-[13px] text-muted">Conta de destino</label>
                <Select<string>
                  value={form.toAccountId ?? ''}
                  options={accounts.filter((a) => a.id !== form.fromAccountId).map(accountOption)}
                  onChange={(id) => update('toAccountId', id)}
                  placeholder="Selecione"
                  aria-label="Conta de destino"
                />
              </div>
            </div>
          ) : cardMode ? (
            <div>
              <label className="mb-1 block text-[13px] text-muted">Cartão</label>
              <Select<string>
                value={form.cardId ?? ''}
                options={cards.map(cardOption)}
                onChange={(id) => update('cardId', id)}
                placeholder="Selecione o cartão"
                aria-label="Cartão"
              />
              {previewInvoiceMonth && (
                <p className="mt-2 flex items-center gap-1.5 rounded-lg bg-cards/10 px-3 py-2 text-[13px] text-cards">
                  <CreditCardIcon className="size-4 shrink-0" />
                  <span>
                    Cai na fatura de{' '}
                    <span className="font-semibold capitalize">
                      {formatMonthLabel(previewInvoiceMonth)}
                    </span>
                    .
                  </span>
                </p>
              )}
              {cardsQuery.isSuccess && cards.length === 0 && (
                <p className="mt-2 text-[13px] text-muted">
                  Nenhum cartão cadastrado ainda. Crie um na tela Cartões.
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="mb-1 block text-[13px] text-muted">Conta</label>
              <Select<string>
                value={form.accountId ?? ''}
                options={accounts.map(accountOption)}
                onChange={(id) => update('accountId', id)}
                placeholder="Selecione a conta"
                aria-label="Conta"
              />
            </div>
          )}

          {/* Mais detalhes */}
          {!isReview && <button
            type="button"
            onClick={() => setDetailsOpen((v) => !v)}
            className="flex min-h-11 items-center gap-1 rounded-full text-sm font-semibold text-primary transition-colors hover:text-primary-hover"
          >
            Mais detalhes
            <ChevronRight
              className={cn('size-4 transition-transform', detailsOpen && 'rotate-90')}
            />
          </button>}

            </div>

          {detailsOpen && (
            <div className="min-w-0 space-y-4 border-t border-border pt-4 lg:border-l lg:border-t-0 lg:pl-5 lg:pt-0">
              {/* Tags (entradas) */}
              {!isTransfer && (
                <div>
                  <label className="mb-1.5 block text-[13px] text-muted">Tags</label>
                  {tags.length === 0 ? (
                    <p className="text-[13px] text-muted">Nenhuma tag cadastrada.</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {tags.map((tag) => {
                        const active = form.tagIds.includes(tag.id);
                        return (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() =>
                              update(
                                'tagIds',
                                active
                                  ? form.tagIds.filter((id) => id !== tag.id)
                                  : [...form.tagIds, tag.id],
                              )
                            }
                            className={cn(
                              'min-h-11 rounded-full border px-3 py-2 text-[13px] font-medium transition-colors',
                              active
                                ? 'border-primary bg-primary-weak text-primary'
                                : 'border-border text-muted hover:border-muted hover:text-ink',
                            )}
                          >
                            {tag.name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Observação */}
              <div>
                <label htmlFor="tx-note" className="mb-1 block text-[13px] text-muted">
                  Observação
                </label>
                <textarea
                  id="tx-note"
                  value={form.note}
                  onChange={(e) => update('note', e.target.value)}
                  maxLength={500}
                  rows={2}
                  placeholder="Anotação opcional"
                  className="w-full resize-none rounded-lg border border-field bg-surface px-3 py-2 text-[15px] text-ink transition-colors focus:border-primary focus:outline-none"
                />
              </div>

              {/* Recorrência (só criação de entradas) */}
              {!isTransfer && !editing && (
                <div className="space-y-3 rounded-xl bg-canvas p-3">
                  <div className="flex items-start gap-2">
                    <Repeat1 className="mt-0.5 size-4 shrink-0 text-muted" />
                    <div className="min-w-0 flex-1">
                      <Toggle
                        checked={form.recurrence === 'fixed'}
                        onChange={(v) =>
                          update('recurrence', v ? 'fixed' : 'none')
                        }
                        accent={meta.accent}
                        label={meta.fixedLabel}
                      />
                      <p className="mt-0.5 text-[12px] text-muted">
                        Repete todo mês com o valor cheio.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-2 border-t border-border pt-3">
                    <Repeat className="mt-0.5 size-4 shrink-0 text-muted" />
                    <div className="min-w-0 flex-1">
                      <Toggle
                        checked={form.recurrence === 'installment'}
                        onChange={(v) =>
                          update('recurrence', v ? 'installment' : 'none')
                        }
                        accent={meta.accent}
                        label="Parcelar"
                      />
                      <p className="mt-0.5 text-[12px] text-muted">
                        Divide o valor em parcelas mensais.
                      </p>
                      {form.recurrence === 'installment' && (
                        <div className="mt-2 flex items-center gap-3">
                          <div className="inline-flex items-center rounded-full border border-border bg-surface">
                            <button
                              type="button"
                              aria-label="Menos parcelas"
                              onClick={() =>
                                update('installments', Math.max(2, form.installments - 1))
                              }
                              className="grid size-11 place-items-center rounded-full text-muted transition-colors hover:text-ink disabled:opacity-40"
                              disabled={form.installments <= 2}
                            >
                              <Minus className="size-4" />
                            </button>
                            <span className="min-w-[3.5rem] text-center text-sm font-semibold tabular-nums text-ink">
                              {form.installments}×
                            </span>
                            <button
                              type="button"
                              aria-label="Mais parcelas"
                              onClick={() =>
                                update('installments', Math.min(120, form.installments + 1))
                              }
                              className="grid size-11 place-items-center rounded-full text-muted transition-colors hover:text-ink disabled:opacity-40"
                              disabled={form.installments >= 120}
                            >
                              <Plus className="size-4" />
                            </button>
                          </div>
                          {form.amountCents > 0 && (
                            <span className="text-[13px] text-muted">
                              {form.installments}× de aprox.{' '}
                              <span className="font-semibold text-ink">
                                R$ {(form.amountCents / form.installments / 100).toFixed(2).replace('.', ',')}
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Não somar nos relatórios (entradas) */}
              {!isTransfer && (
                <Toggle
                  checked={form.ignoreInReports}
                  onChange={(v) => update('ignoreInReports', v)}
                  accent="primary"
                  label="Não incluir nos relatórios"
                />
              )}
            </div>
          )}
          </div>

          {/* Rodapé */}
          <div data-testid="transaction-footer" className="sticky bottom-0 z-10 -mx-1 mt-4 flex flex-col-reverse items-stretch gap-2 border-t border-border bg-surface px-1 pb-1 pt-3 sm:flex-row sm:items-center sm:justify-end">
            {!editing && !isReview && (
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={!canSave || mutation.isPending || reviewSaving}
                className="h-11 rounded-full px-4 text-[13px] font-semibold uppercase tracking-wide text-primary transition-colors hover:bg-primary-weak disabled:cursor-not-allowed disabled:opacity-50"
              >
                Salvar e criar nova
              </button>
            )}
            <button
              type="submit"
              disabled={!canSave || mutation.isPending || reviewSaving}
              className={cn(
                'flex h-11 items-center justify-center gap-2 rounded-full px-6 text-sm font-semibold uppercase tracking-wide text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50',
                meta.btn,
              )}
            >
              {(mutation.isPending || reviewSaving) && <Loader2 className="size-4 animate-spin" />}
              {isReview ? 'Salvar revisão' : 'Salvar'}
            </button>
          </div>
        </form>
      </Sheet>

      <RecurrenceScopeDialog
        open={scopeOpen}
        title="Editar lançamento recorrente"
        confirmLabel="Salvar"
        loading={mutation.isPending}
        onConfirm={(scope) => mutation.mutate({ scope, another: false })}
        onCancel={() => setScopeOpen(false)}
      />
    </>
  );
}
